import { useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { faGraduationCap } from '@fortawesome/pro-solid-svg-icons'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { RankmiTable } from '@rankmi/rankmi-table'
import { sortTableEnum } from 'enum/sortTable.enum'
import {
  LEARNING_PATHS,
  QueryLearningPathsArgs,
  TDataLearningPaths,
} from 'graphqlAPI'
import {
  generateTableLocale,
  joinKeys,
  useTranslationRankmi,
} from 'helpers/translations.helper'
import { usePaginationVariables } from 'hooks/usePaginationVariables'
import { SortValue } from 'models/rankmiTable.model'
import { SuccessToast } from 'components/ui/toast/toastsTemplates'
import download from '../../../../api/learning-paths/individualReport'
import { useColumns } from './hooks/useColumns'
import { prefixTable } from './hooks/useColumns/constants'
import styles from './LearningPaths.module.scss'
import { ApplyFiltersByType } from '../../types'
import { ChildFilterTypes } from 'enum'
import { IAreaModel } from 'models/area.model'

type Args = QueryLearningPathsArgs & { areaId?: string }

interface LearningPathsProps {
  area?: IAreaModel | null
  applyFiltersByType: ApplyFiltersByType
}

export const LearningPaths = ({
  area,
  applyFiltersByType,
}: LearningPathsProps) => {
  const { text, perPage, page, sorts, setPerPage, setPage, setSorts, setText } =
    usePaginationVariables()
  const areaId = area?.id?.toString()

  const g = useMemo(() => {
    const { filters } = applyFiltersByType(ChildFilterTypes.LearningPath)
    return filters || []
  }, [applyFiltersByType])

  const variables = useMemo(
    (): Args => ({
      areaId,
      perPage,
      page,
      search: text,
      q:
        sorts.length || g.length
          ? {
              sorts,
              g,
            }
          : undefined,
    }),
    [areaId, perPage, page, text, sorts, g]
  )

  useEffect(() => {
    setPage(1)
  }, [g, setPage])
  const { data: dataLearningPaths, loading: loadingLearningPaths } = useQuery<
    TDataLearningPaths,
    Args
  >(LEARNING_PATHS, {
    variables,
    fetchPolicy: 'cache-and-network',
  })


  const loading = loadingLearningPaths
  const learningPaths = dataLearningPaths?.learningPaths

  const total = learningPaths?.nodesCount || 0

  const onDownload = (id: string) => {
    if (id && id.trim() !== '') {
      download(id).then((response) => {
        if (response.status === 200) {
          SuccessToast(tRelativeToasts('downloadSent'))
        } else {
          console.log('Error')
        }
      })
    }
  }

  const columns = useColumns({ onDownload })

  const dataSource = useMemo(
    () =>
      learningPaths?.nodes.map((user) => ({
        ...user,
        key: user.id,
      })) || [],
    [learningPaths]
  )
 
  const tRelative = useTranslationRankmi({
    prefix: joinKeys(prefixTable),
  })

  const tRelativeToasts = useTranslationRankmi({
    prefix: 'administration.toastMessages',
  })

  const locale = useMemo(
    () =>
      generateTableLocale({
        header: {
          rowsCount: tRelative('rowsCount'),
          rowsCount_plural: tRelative('rowsCount_plural'),
        },
      }),
    [tRelative]
  )

  return (
    <Row className={classNames('mt-4', styles.root)}>
      <Col xs={12}>
        <RankmiTable
          columnHeaders={columns}
          data={dataSource}
          total={total}
          currentPage={page}
          changeColumnsVisibility
          onPaginationChange={(page: any) => setPage(page)}
          onSearch={(text: string) => setText(text)}
          search={text}
          onSortChange={({ field, order }: SortValue) =>
            setSorts(order ? [`${field} ${sortTableEnum[order]}`] : [])
          }
          loading={loading}
          onRowsPerPageChange={setPerPage}
          countIcon={faGraduationCap}
          initialRowsPerPage={10}
          responsive
          locale={locale}
        />
      </Col>
    </Row>
  )
}
